import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useOutletContext, useParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient, currentTenantId } from '../api.js';
import { ContextActions } from '../components/ContextActions.jsx';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { EditIcon, SaveIcon, XIcon } from '../components/icons';
import { rememberProjectContext } from '../hooks/useProjectContext.js';
import { SetupChecklistCard } from '../components/SetupChecklistCard.jsx';

/**
 * Project-scoped layout.
 *
 * Sits between the tenant shell and each project sub-page.  Loads
 * the project by slug, renders the header, and hands the rest to
 * the nested route via <Outlet>.  Each child gets the loaded
 * project via useOutletContext() if it needs it (we keep the prop
 * passing minimal for now).
 */
export function ProjectDetail() {
  const { slug } = useParams();
  const { getToken } = useAuth();
  const api = useMemo(() => createApiClient(getToken), [getToken]);

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api('/v1/admin/projects');
        const list = Array.isArray(data) ? data : (data?.items || []);
        const match = list.find((p) => p.slug === slug) || null;
        if (alive) {
          setProject(match);
          if (!match) setError(`No project with slug "${slug}" in this tenant.`);
          // Card 064 — push the resolved project name into the
          // session-sticky context so personal-page detours (e.g.
          // /my-keys, /audit) can offer a "Back to <project>"
          // bounce-back without re-resolving the slug.  Card 068 —
          // the cache is per-tenant; if the admin call succeeded
          // we know the tenant id we're writing under is the live
          // one because `createApiClient` sent it on the request
          // that just resolved.
          if (match) {
            rememberProjectContext(currentTenantId(), {
              slug: match.slug,
              name: match.name,
            });
          }
        }
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [api, slug]);

  if (loading) {
    return (
      <div className="page">
        <div className="state"><div className="state__title">Loading project…</div></div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="page">
        <div className="page__title"><div><h1>Project</h1></div></div>
        <div className="state state--error">
          <div className="state__title">Couldn't open project</div>
          <div>{error || `No project matches ${slug}`}.</div>
          <Link to="/projects" className="btn btn--secondary" style={{ marginTop: 12 }}>
            Back to projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page__title">
        <div style={{ flex: 1 }}>
          <div className="breadcrumb">
            <Link to="/projects">Projects</Link>
            <span>/</span>
            <span>{project.name}</span>
          </div>
          <EditableProjectHeader project={project} api={api} onSaved={setProject} />
          <p className="page__subtitle">
            <code>{project.slug}</code>
            {project.is_default && (
              <>
                {' '}· <span className="badge badge--default" title="Marked is_default. This is a historical flag — every request now must carry an explicit X-Spotlight-Project header (or a project-scoped API key), so 'default' no longer changes routing.">
                  marked default
                </span>
              </>
            )}
          </p>
        </div>
      </div>

      <Outlet context={{ project }} />
      <style>{PROJECT_CSS}</style>
    </div>
  );
}

/** Project header that turns into a name + description editor on click.
 *  Slug is intentionally read-only — changing it would break every
 *  bookmarklet / API-key install pointing at the old URL.
 */
function EditableProjectHeader({ project, api, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setName(project.name);
    setDescription(project.description || '');
  }, [project.name, project.description]);

  async function save() {
    if (!name.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const updated = await api(`/v1/admin/projects/${encodeURIComponent(project.project_id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: name.trim(), description: description.trim() }),
      });
      onSaved?.(updated);
      setEditing(false);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!editing) {
    return (
      <h1 className="project-head">
        <span>{project.name}</span>
        {project.is_default && (
          <span className="badge badge--default" style={{ marginLeft: 10, verticalAlign: 'middle' }}>
            Default
          </span>
        )}
        <button type="button" className="project-head__edit" onClick={() => setEditing(true)} title="Rename this project" aria-label="Rename project">
          <EditIcon /> Edit
        </button>
        {project.description && (
          <div className="project-head__desc">{project.description}</div>
        )}
      </h1>
    );
  }

  return (
    <div className="project-head project-head--editing">
      <input
        className="input project-head__name-input"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Project name"
        maxLength={80}
        autoFocus
      />
      <input
        className="input project-head__desc-input"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Optional description (one line)"
        maxLength={280}
      />
      {err && <div className="project-head__err">{err}</div>}
      <div className="project-head__actions">
        <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => { setEditing(false); setName(project.name); setDescription(project.description || ''); setErr(null); }}>
          <XIcon /> Cancel
        </button>
        <button type="button" className="btn btn--primary" disabled={busy || !name.trim()} onClick={save}>
          <SaveIcon /> {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

export function ProjectOverview() {
  // Default landing inside a project.  Each card is a live link to
  // the project-scoped list view; authoring still happens in the
  // admin overlay, which the bookmarklet launches.
  const { slug } = useParams();
  const { project } = useOutletContext() || {};
  return (
    <>
      {/* Card 090 — project setup checklist sits above ContextActions
          so the operator sees outstanding wiring (no embed key, no
          allow-listed site, etc.) before the per-project nudge strip
          and the breaker toggle. Self-collapses to a "✅ Setup
          complete" pill once every probe ticks. */}
      {project?.project_id && (
        <SetupChecklistCard scope="project" projectId={project.project_id} />
      )}
      <ProjectContextActions project={project} slug={slug} />
      <CircuitBreakerToggle project={project} slug={slug} />
      <ProjectCardGrid slug={slug} />
    </>
  );
}

/** Per-project "what needs you" strip.  Surfaces help-inbox volume
 *  scoped to this project, plus pointers if the project hasn't been
 *  set up yet (no API keys, no tours).
 */
function ProjectContextActions({ project, slug }) {
  const { getToken } = useAuth();
  const api = useMemo(() => createApiClient(getToken), [getToken]);
  const [help, setHelp] = useState({ open: 0, mine: 0 });
  const [tourCount, setTourCount] = useState(null);
  const [keyCount, setKeyCount] = useState(null);

  useEffect(() => {
    if (!project?.project_id) return undefined;
    let alive = true;
    const safe = (p) => api(p).catch(() => null);
    (async () => {
      // Card 092 — these used to hit path-scoped endpoints that
      // don't exist (/v1/admin/projects/<id>/tours etc.) and 404'd
      // on every project load.  The real shape is the flat list
      // route with X-Spotlight-Project header (or project_id query
      // string).  The api client doesn't auto-stamp the header here
      // so we pass the slug on the query string instead.
      const [helpInbox, tours, keys] = await Promise.all([
        safe(`/v1/admin/help/requests?project_id=${encodeURIComponent(project.project_id)}&status=open`),
        safe(`/v1/admin/tours?project_id=${encodeURIComponent(project.project_id)}`),
        safe(`/v1/admin/api-keys?project_id=${encodeURIComponent(project.project_id)}`),
      ]);
      if (!alive) return;
      const openItems = helpInbox?.items || [];
      setHelp({ open: openItems.length, mine: 0 });
      setTourCount(Array.isArray(tours?.items) ? tours.items.length : (Array.isArray(tours) ? tours.length : null));
      setKeyCount(Array.isArray(keys?.items) ? keys.items.length : (Array.isArray(keys) ? keys.length : null));
    })();
    return () => { alive = false; };
  }, [api, project?.project_id]);

  const actions = useMemo(() => {
    const out = [];
    if (help.open > 0) {
      out.push({
        id: 'project-help',
        title: help.open === 1
          ? `1 open help request on ${project?.name || 'this project'}`
          : `${help.open} open help requests on ${project?.name || 'this project'}`,
        body: 'Filter the inbox to just this project to triage.',
        cta: 'Open inbox',
        // Card 064 — stay project-scoped.  /help bounces to the
        // tenant-wide inbox; the project route lands on the same
        // surface pre-filtered to this project.
        to: `/projects/${slug}/help/inbox`,
        tone: 'warn',
      });
    }
    if (keyCount === 0) {
      out.push({
        id: 'mint-key',
        title: 'No API keys yet for this project',
        body: 'Mint one to embed the SDK on a page. The key carries this project\'s scope automatically.',
        cta: 'Mint a key',
        to: `/projects/${slug}/api-keys`,
        tone: 'primary',
      });
    }
    if (tourCount === 0 && keyCount > 0) {
      out.push({
        id: 'first-tour',
        title: 'Build your first tour on this project',
        body: 'Five clicks in the bookmarklet ship one.  Walk new users through the parts of your product that matter.',
        cta: 'Open the bookmarklet',
        to: `/projects/${slug}/bookmarklet`,
        tone: 'normal',
      });
    }
    return out;
  }, [help.open, keyCount, tourCount, project?.name, slug]);

  return <ContextActions actions={actions} />;
}

function CircuitBreakerToggle({ project, slug }) {
  const { getToken } = useAuth();
  const api = useMemo(
    () => createApiClient(getToken, { projectId: project?.project_id || null }),
    [getToken, project?.project_id],
  );
  const [tripped, setTripped] = useState(!!project?.circuit_breaker);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [confirmNode, ask] = useConfirm();

  useEffect(() => { setTripped(!!project?.circuit_breaker); }, [project?.circuit_breaker]);

  async function applyToggle(next) {
    setBusy(true);
    setErr(null);
    try {
      await api(`/v1/admin/projects/${encodeURIComponent(project.project_id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ circuit_breaker: next }),
      });
      setTripped(next);
    } catch (e) {
      setErr(e.message);
      setTripped(!next);
      throw e;
    } finally {
      setBusy(false);
    }
  }

  function toggle(next) {
    if (!project?.project_id) return;
    if (next) {
      ask({
        title: `Trip the circuit breaker for ${project.name}?`,
        body: 'The SDK will stop serving content for this project until you flip it back. Useful to pause a bad rollout without touching code.',
        confirmLabel: 'Trip breaker',
        danger: true,
        runOnConfirm: () => applyToggle(true),
      });
      return;
    }
    applyToggle(false);
  }

  return (
    <section className={`card breaker${tripped ? ' breaker--tripped' : ''}`} style={{ marginTop: 4 }}>
      <div className="breaker__head">
        <div>
          <h3 className="card__title" style={{ margin: 0 }}>
            {tripped ? 'Circuit breaker TRIPPED' : 'Circuit breaker'}
          </h3>
          <p className="card__body" style={{ margin: '4px 0 0' }}>
            {tripped ? (
              <>This project is paused. End-user pages get an empty content
              response until you flip this back on.</>
            ) : (
              <>Kill switch for this project only. Flip it if a bad publish
              or selector drift is affecting real users and you need to pause
              without a deploy.</>
            )}
          </p>
        </div>
        <label className="breaker__switch">
          <input
            type="checkbox"
            checked={tripped}
            disabled={busy}
            onChange={(e) => toggle(e.target.checked)}
          />
          <span>{tripped ? 'Tripped' : 'Active'}</span>
        </label>
      </div>
      {err && <p style={{ color: 'var(--danger-700)', marginTop: 8 }}>{err}</p>}
      {confirmNode}
      <style>{BREAKER_CSS}</style>
    </section>
  );
}

function ProjectCardGrid({ slug }) {
  return (
    <div className="card-grid" style={{ marginTop: 4 }}>
      <Link to={`/projects/${slug}/tours`} className="quick-card">
        <div className="quick-card__title">Tours</div>
        <p className="quick-card__body">
          Multi-step product tours. See every tour in this project
          with its status, version, and step count.
        </p>
      </Link>
      <Link to={`/projects/${slug}/content`} className="quick-card">
        <div className="quick-card__title">Content</div>
        <p className="quick-card__body">
          Spotlights, tooltips, hotspots, modals, banners. One
          surface per published piece of content.
        </p>
      </Link>
      <Link to={`/projects/${slug}/checklists`} className="quick-card">
        <div className="quick-card__title">Checklists</div>
        <p className="quick-card__body">
          Onboarding checklists — persistent task lists that the SDK
          ticks off as users complete each step.
        </p>
      </Link>
      <Link to={`/projects/${slug}/themes`} className="quick-card">
        <div className="quick-card__title">Themes</div>
        <p className="quick-card__body">
          CSS-variable palettes applied to every Spotlight surface
          on this project.
        </p>
      </Link>
      <Link to={`/projects/${slug}/api-keys`} className="quick-card">
        <div className="quick-card__title">API keys</div>
        <p className="quick-card__body">
          Mint project-scoped keys. One per page/deploy keeps blast
          radius tiny if a bundle leaks — revoke without touching
          other projects.
        </p>
      </Link>
      <Link to={`/projects/${slug}/sites`} className="quick-card">
        <div className="quick-card__title">Sites</div>
        <p className="quick-card__body">
          Origin allow-list for this project. Pages not on the list
          get <code>403 Origin not allowed</code> from the SDK edge.
        </p>
      </Link>
      <Link to={`/projects/${slug}/webhooks`} className="quick-card">
        <div className="quick-card__title">Webhooks</div>
        <p className="quick-card__body">
          HMAC-signed callbacks for this project's content events.
          Scoped so a receiver here doesn't see firing on other
          projects.
        </p>
      </Link>
      <Link to={`/projects/${slug}/analytics`} className="quick-card">
        <div className="quick-card__title">Analytics</div>
        <p className="quick-card__body">
          Forward events to PostHog, Segment, GA4, etc. Per-project
          override — tenants can have different pipelines for
          different projects.
        </p>
      </Link>
      <Link to={`/projects/${slug}/install`} className="quick-card">
        <div className="quick-card__title">Install</div>
        <p className="quick-card__body">
          Copy the production <code>&lt;script&gt;</code> snippet for
          your site's <code>&lt;head&gt;</code>. Uses this project's
          publishable <code>pk_live_*</code> embed key — safe to ship
          in page source.
        </p>
      </Link>
      <Link to={`/projects/${slug}/bookmarklet`} className="quick-card">
        <div className="quick-card__title">Bookmarklet</div>
        <p className="quick-card__body">
          Drag this project's admin-mode bookmarklet into your bar.
          Click on any page, Spotlight boots in admin for this project
          and the overlay opens for authoring.
        </p>
      </Link>
    </div>
  );
}

const BREAKER_CSS = `
  .breaker {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .breaker__head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 14px;
  }
  .breaker--tripped {
    border-color: var(--danger-50);
    background: linear-gradient(180deg, var(--danger-50) 0%, var(--surface-0) 100%);
  }
  .breaker--tripped .card__title { color: var(--danger-700); }

  .breaker__switch {
    display: inline-flex;
    gap: 8px;
    align-items: center;
    cursor: pointer;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--text-2);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .breaker__switch input {
    width: 42px;
    height: 22px;
    appearance: none;
    -webkit-appearance: none;
    background: var(--surface-muted);
    border-radius: var(--r-pill);
    position: relative;
    cursor: pointer;
    transition: background 140ms ease;
  }
  .breaker__switch input::before {
    content: "";
    position: absolute;
    width: 18px;
    height: 18px;
    background: #fff;
    border-radius: 50%;
    top: 2px;
    left: 2px;
    transition: left 140ms ease;
  }
  .breaker__switch input:checked { background: var(--danger-600); }
  .breaker__switch input:checked::before { left: 22px; }
`;

const PROJECT_CSS = `
  /* Inline-editable project header. */
  .project-head {
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    font-size: 1.75rem;
    font-weight: 700;
    color: var(--text-1, #0f172a);
    line-height: 1.2;
  }
  .project-head__desc {
    flex-basis: 100%;
    font-size: 13.5px;
    font-weight: 400;
    color: var(--text-3, #64748b);
    margin-top: 2px;
  }
  .project-head__edit {
    border: 0;
    background: transparent;
    color: var(--brand-600, #4f46e5);
    font: inherit;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 6px;
    opacity: 0;
    transition: opacity 120ms ease, background 120ms ease;
  }
  .project-head:hover .project-head__edit,
  .project-head__edit:focus-visible { opacity: 1; }
  .project-head__edit:hover { background: var(--surface-1, #f1f5f9); }

  .project-head--editing {
    display: flex; flex-direction: column; gap: 6px;
    padding: 12px 14px;
    background: var(--surface-1, #f8fafc);
    border: 1px solid var(--border-1, #e5e7eb);
    border-radius: 10px;
    max-width: 540px;
  }
  .project-head__name-input {
    font-size: 1.4rem;
    font-weight: 700;
    padding: 6px 8px;
  }
  .project-head__desc-input {
    font-size: 13px;
    padding: 6px 8px;
  }
  .project-head__err { color: var(--danger-700, #b91c1c); font-size: 12.5px; }
  .project-head__actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 4px; }

  .breadcrumb {
    display: flex;
    gap: 6px;
    align-items: center;
    font-size: 12.5px;
    color: var(--text-3);
    margin-bottom: 4px;
  }
  .breadcrumb a {
    color: var(--text-3);
    text-decoration: none;
  }
  .breadcrumb a:hover {
    color: var(--text-2);
    text-decoration: underline;
  }
  .breadcrumb span:last-child {
    color: var(--text-2);
    font-weight: 500;
  }
`;
